import { Button, ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { IconSizes } from "components/icons/constants/icon-sizes";
import { Icons } from "components/icons/constants/icons";
import { Icon } from "components/icons/icon";
import { UserRoleRecord } from "models/view-models/user-roles/user-role-record";
import React, { JSX, useCallback, useMemo } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useIdentity } from "utilities/hooks/use-identity";
import {
    UserLoginService,
} from "utilities/services/users/user-login-service";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { sitemap } from "sitemap";
import { RouteUtils } from "utilities/route-utils";
import { useNavigate } from "utilities/hooks/navigation/use-navigate";
import "./user-role-card.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UserRoleCardProps {
    userRole: UserRoleRecord;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "user-role-card";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Components
// -------------------------------------------------------------------------------------------------

const UserRoleCard: React.FC<UserRoleCardProps> = (
    props: UserRoleCardProps
): JSX.Element | null => {
    const { record: globalState, setRecord: setGlobalState } = useGlobalState();
    const { update: updateUserLoginApi } = UserLoginService.useUpdate();
    const { getIdentity } = useIdentity();
    const navigate = useNavigate();

    const handleSwitchRole = useCallback(async () => {
        if (!globalState.isAuthenticated() || !globalState.currentIdentity?.hasUserLoginId()) {
            return;
        }

        try {
           
            try {
                //First go to the switch profile page.  Update the role there.
                navigate(
                    RouteUtils.replacePathParams(sitemap.authenticated.switchProfile, {
                        roleType: props.userRole.role?.roleType,
                        roleId: props.userRole.roleId,
                    })
                );
            } catch {
                ToastManager.error(t("thereWasAnIssueLoadingUserInfo"));
            }
        } catch {
            ToastManager.error(t("thereWasAnIssueSwitchingProfiles"));
            setGlobalState((currentState) => currentState.setUnauthenticated());
        }
    }, [
        getIdentity,
        globalState,
        navigate,
        props.userRole.role?.roleType,
        props.userRole.roleId,
        setGlobalState,
        updateUserLoginApi,
    ]);

    const isCurrentRole: boolean = useMemo((): boolean => {
        return globalState.currentIdentity?.role?.id === props.userRole.roleId;
    }, [globalState.currentIdentity?.role?.id, props.userRole.roleId]);

    const selectionContent: JSX.Element = useMemo(
        (): JSX.Element =>
            isCurrentRole ? (
                <p
                    className={`${CSS_CLASS_NAME}__selected`}
                    data-testid={"user-role-card:selected:lbl:" + props.userRole.roleId}>
                    {t("selected")}
                </p>
            ) : (
                <Button
                    dataTestId={"user-role-card:select:btn:" + props.userRole.roleId}
                    onClick={handleSwitchRole}
                    size={ButtonSize.Medium}
                    style={ButtonStyle.Secondary}
                    text={t("select")}
                />
            ),
        [handleSwitchRole, isCurrentRole]
    );

    return (
        <div className={CSS_CLASS_NAME}>
            <div className={`${CSS_CLASS_NAME}__content`}>
                <Icon
                    cssClassName={`${CSS_CLASS_NAME}__icon`}
                    size={IconSizes.XLarge}
                    type={Icons.PersonOutline}
                />
                <p className={`${CSS_CLASS_NAME}__name`}>
                    {props.userRole.role?.name?.toLocaleUpperCase()}
                </p>
            </div>
            {selectionContent}
        </div>
    );
};

// #endregion Components

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { UserRoleCard };

// #endregion Exports
